import { useNavigate, useParams } from 'react-router';
import { Box, Center, Column, CustomSpinner, Row, Text } from '~/commonComponents';
import { useGetJobPostDetail } from '~/domain/PermanentJobs';
import { useUserSelector } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import { IUser, ProfessionTypeEnum } from '~/models';
import { Colors } from '~/themes/colors';
import { OnsiteInterviewForm } from '../components';
import { useGetUnavailableDates, useSafeLocationState, useStoreOnsiteInterview } from './hooks';
import { TCreateOnsiteInterviewPageRoutes } from './Types';
import { TJobOverviewPageRoutesLocationState } from '../JobOverview';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreateOnsiteInterviewPageRoutes>();
  const navigate = useNavigate();

  const { data, isLoading } = useGetJobPostDetail(id);
  const { isAfterJobPosting } = useSafeLocationState();

  const currentUser: IUser = useUserSelector();

  const unavailableDates = useGetUnavailableDates(id);
  const { onSubmit } = useStoreOnsiteInterview(id);

  if (isLoading || !id) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  return (
    <Column marginInline="auto" width={680}>
      {isAfterJobPosting ? (
        <Row
          background={Colors.warning_30}
          borderColor={Colors.warning_700}
          borderStyle="solid"
          borderWidth={1}
          gap={6}
          marginBottom={20}
          paddingBlock={8}
          paddingInline={12}
        >
          <Box height={24} width={24}>
            <WarningSolidIcon color={Colors.warning_500} size={24} />
          </Box>
          <Text color={Colors.warning_700}>
            Candidates can’t apply until you post an Onsite Interview. Add one now to start
            <br />
            receiving applications.
          </Text>
        </Row>
      ) : (
        <Column marginBottom={16} gap={8}>
          <Text bold color={Colors.primary_500} fontSize={20}>
            Schedule Onsite Interviews
          </Text>
          <Text color={Colors.neutral_500} italic>
            Add onsite interviews as part of your process. They’re designed to help you evaluate a
            candidate’s skills and team compatibility before hiring.
          </Text>
        </Column>
      )}
      <OnsiteInterviewForm
        defaultValues={{
          breakTime: null,
          dates: [],
          hourlyRate: '',
          hoursArrival: '',
          hoursDeparture: '',
          isAssistedHygiene: currentUser.office_info.isAssistedHygiene,
          isPaidBreakTime: false,
          profession: data?.subProfession || data?.professionType || ProfessionTypeEnum.RDH,
          specialty: data?.officeSpecialty || '',
        }}
        jobTitle={data?.title || ''}
        office={data?.officeName || ''}
        profession={data?.subProfession || data?.professionType || ProfessionTypeEnum.RDH}
        unavailableDates={unavailableDates}
        onSubmit={onSubmit}
        {...(isAfterJobPosting
          ? {
              btnSecondaryText: 'I’ll Do It Later',
              btnSecondaryClick: () =>
                navigate(`/permanent-jobs/job-overview/${id}/job-details`, {
                  state: {
                    fromOnsiteInterviewCreation: true,
                  } satisfies TJobOverviewPageRoutesLocationState,
                }),
            }
          : {})}
      />
    </Column>
  );
};
